import React, { useEffect, useState } from "react";
import { Button, Grid, TextField } from "@mui/material";
import SoftBox from "components/SoftBox";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import CsvUploadModel from "../CsvUploadModel";
import { uploadImageToGhl } from "hook/services";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ReactCropModal from "layouts/super-admin/settings/Configurator/components/ReactCropModal/ReactCropModal";
import SoftTypography from "components/SoftTypography";
import PrimaryButton from "../../../components/PrimaryButton";
import "./accountsListingLabels.css";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

function Tags({ fetchData, tagsData }) {
  const [agency] = useAgencyInfo();
  const AppService = useAppServices();
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", currency: "", tag_img: "" });
  const [editIndex, setEditIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editedState, setEditedState] = useState();
  const [imageUploading, setImageUploading] = useState(false);
  const [currentItems, setCurrentItems] = useState(tagsData);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentImageType, setCurrentImageType] = useState("");
  const [tempImageFile, setTempImageFile] = useState(null);

  const sorted = tagsData.sort((a, b) => a.order - b.order);
  useEffect(() => {
    setCurrentItems(sorted);
  }, [sorted]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (index, item) => {
    setEditedState(item?._id);
    if (index >= 0) {
      setFormData({ name: item.name, currency: item.currency, tag_img: item.tag_img });
      setEditIndex(index);
    } else {
      setFormData({ name: "", currency: "", tag_img: "" });
      setEditIndex(null);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openCropModal = (file) => {
    setTempImageFile(file);
    setCurrentImage(URL.createObjectURL(file));
    setCropModalOpen(true);
  };

  const handleCroppedImage = async (blob) => {
    if (!blob) return;

    setImageUploading(true);
    try {
      const file = new File([blob], "cropped-image.jpg", { type: "image/jpeg" });
      const image_response = await uploadImageToGhl(file, agency);

      if (image_response?.data) {
        setFormData((prev) => ({
          ...prev,
          tag_img: image_response.data,
        }));
      }
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setImageUploading(false);
      setCropModalOpen(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      openCropModal(file);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    let payload;

    if (editIndex != null) {
      payload = {
        _id: editedState,
        ...formData,
        agency_id: agency._id,
      };
    } else {
      payload = {
        ...formData,
        agency_id: agency._id,
      };
    }

    let response;
    if (editIndex != null) {
      response = await AppService.business_tags.update({ payload });
    } else {
      response = await AppService.business_tags.create({ payload });
    }

    if (response) {
      fetchData();
      handleCloseModal();
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    const response = await AppService.business_tags.delete({ query: `_id=${id}` });
    if (response) {
      fetchData();
    }
    setLoading(false);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = reorder(currentItems, result.source.index, result.destination.index);
    setCurrentItems(reordered);

    const reorderedPayload = reordered.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    const payload = {
      reorderItems: reorderedPayload,
    };

    await AppService.business_tags.ReOrderTags({
      payload,
    });
  };

  return (
    <div className="sd-accounts-labels">
      <SoftBox component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3} paddingX={3} paddingBottom={4}>
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Grid item xs={12} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
              <CsvUploadModel handleRefresh={fetchData} />
              <PrimaryButton showPlus type="button" onClick={() => handleOpenModal(-1)}>
                Add Listing Label
              </PrimaryButton>
            </Grid>
          </Grid>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-tags" direction="vertical">
              {(provided) => (
                <Grid
                  item
                  xs={12}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ marginTop: "20px" }}
                >
                  {currentItems.map((category, index) => (
                    <Draggable key={category._id} draggableId={category._id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          className="sd-accounts-labels__tag-row"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "10px",
                            backgroundColor: snapshot.isDragging ? "#e0e0e0" : "#f8f9fa",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            {category.tag_img && (
                              <img
                                src={category.tag_img}
                                alt="Tag"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            )}
                            <p>{category.name}</p>
                          </div>

                          <p>{category.currency}</p>

                          <div style={{ display: "flex", gap: "10px" }}>
                            <PrimaryButton
                              type="button"
                              onClick={() => handleOpenModal(index, category)}
                            >
                              Edit
                            </PrimaryButton>
                            <PrimaryButton
                              type="button"
                              className="sd-primary-btn--dark"
                              onClick={() => handleDelete(category._id)}
                              disabled={loading}
                            >
                              Delete
                            </PrimaryButton>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Grid>
              )}
            </Droppable>
          </DragDropContext>
        </Grid>

        {openModal && (
          <div className="modal-zz-confirm text-left" onClick={handleCloseModal}>
            <div
              className="scrollable-modal-content"
              style={{
                padding: "20px",
                backgroundColor: "white",
                borderRadius: "10px",
                position: "relative",
                maxWidth: "500px",
                width: "90%",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2>{editIndex !== null ? "Edit Listing Label" : "Add Listing Label"}</h2>
              <span className="crossison" onClick={handleCloseModal}>
                X
              </span>

              <div style={{ marginTop: "16px", marginBottom: "16px" }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  id="tag-image-upload"
                  style={{ display: "none" }}
                />
                <label htmlFor="tag-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    style={{ marginBottom: "16px", color: "black", borderColor: "black" }}
                  >
                    Upload Image
                  </Button>
                </label>
                <Grid item xs={12}>
                  <SoftTypography variant="body2">
                    1MB maximum size. 1:1 ratio. Recommended dimensions: 512 x 512 pixels.
                  </SoftTypography>
                </Grid>

                {formData.tag_img && (
                  <div style={{ marginTop: "10px" }}>
                    <img
                      src={formData.tag_img}
                      alt="Tag preview"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                )}
              </div>

              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData?.name}
                onChange={handleInputChange}
                margin="normal"
                required
              />

              {/* <TextField
                fullWidth
                label="Currency"
                name="currency"
                value={formData?.currency}
                onChange={handleInputChange}
                margin="normal"
              /> */}

              <Button
                type="submit"
                variant="contained"
                className="btn-newdezine"
                disabled={loading || imageUploading}
                style={{
                  background: "black",
                  color: "white",
                  marginTop: "20px",
                  width: "100%",
                  padding: "10px",
                }}
              >
                {imageUploading
                  ? "Uploading..."
                  : loading
                  ? "Processing..."
                  : editIndex !== null
                  ? "Update"
                  : "Add"}
              </Button>
            </div>
          </div>
        )}
      </SoftBox>

      <ReactCropModal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        currentImage={currentImage}
        onCropComplete={handleCroppedImage}
        aspectRatio={1}
      />
    </div>
  );
}

export default Tags;
